import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import type { Analytics } from "../../api";
import { fmtInt } from "../../lib/format";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Progress } from "../ui/progress";

/**
 * Mijozlar bilan ishlash — Telegram ulanishi + eslatmalar voronkasi bitta
 * ixcham kartada.
 *
 * Ilgari bular ikkita to'liq karta edi: donut diagramma (ikki sonni bo'yardi)
 * va radial "gauge" (bitta foizni ko'rsatardi). Ikkalasi ham `Progress`
 * chizig'i aytadigan gapni takrorlardi, lekin daromad va mijoz qiymati bilan
 * BIR XIL vizual og'irlikda turardi. Endi ular ikkinchi darajali.
 */
export function EngagementCard({
  clients,
  reminders,
  funnel,
  onOpenReminders,
}: {
  clients: Analytics["clients"];
  reminders: Analytics["reminders"];
  funnel: Analytics["reminder_funnel"];
  onOpenReminders?: () => void;
}) {
  const { t } = useTranslation("analytics");
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base">{t("engagement.title")}</CardTitle>
          {onOpenReminders && (
            <Button variant="ghost" size="sm" onClick={onOpenReminders}>
              {t("reminderFunnel.openReminders")}
              <ArrowRight />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="grid gap-5 sm:grid-cols-2">
        {/* ===== Telegram ===== */}
        <div className="space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs font-medium text-muted-foreground">
              {t("telegram.title")}
            </span>
            <strong className="font-num text-lg tabular-nums">
              {clients.telegram_pct.toFixed(1)}%
            </strong>
          </div>
          <Progress
            value={clients.telegram_pct}
            indicatorClassName="bg-chart-positive"
            className="h-1.5"
          />
          <dl className="text-xs text-muted-foreground space-y-1">
            <div className="flex justify-between gap-2">
              <dt>{t("telegram.connected")}</dt>
              <dd className="tabular-nums text-foreground">
                {fmtInt(clients.with_telegram)}
              </dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>{t("telegram.notConnected")}</dt>
              <dd className="tabular-nums text-foreground">
                {fmtInt(clients.without_telegram)}
              </dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>{t("telegram.linkedViaQr")}</dt>
              <dd className="tabular-nums text-foreground">
                {fmtInt(clients.linked_via_qr_30d)}
              </dd>
            </div>
          </dl>
        </div>

        {/* ===== Eslatmalar ===== */}
        <div className="space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs font-medium text-muted-foreground">
              {t("reminderFunnel.title")}
            </span>
            {funnel && funnel.created > 0 && (
              <strong className="font-num text-lg tabular-nums">
                {funnel.success_pct.toFixed(0)}%
              </strong>
            )}
          </div>
          {funnel && funnel.created > 0 ? (
            <Progress
              value={funnel.success_pct}
              indicatorClassName="bg-chart-positive"
              className="h-1.5"
            />
          ) : (
            <p className="text-xs text-muted-foreground">{t("empty.noInfo")}</p>
          )}
          <dl className="text-xs text-muted-foreground space-y-1">
            <div className="flex justify-between gap-2">
              <dt>{t("reminderFunnel.sent")}</dt>
              <dd className="tabular-nums text-foreground">{fmtInt(reminders.sent)}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>{t("reminderFunnel.pending")}</dt>
              <dd className="tabular-nums text-foreground">{fmtInt(reminders.pending)}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>{t("reminderFunnel.failed")}</dt>
              <dd
                className={
                  "tabular-nums " +
                  (reminders.failed > 0 ? "text-chart-negative font-medium" : "text-foreground")
                }
              >
                {fmtInt(reminders.failed)}
              </dd>
            </div>
          </dl>
        </div>
      </CardContent>
    </Card>
  );
}
